import locale from "@bonliva-crm/locale";
import { Box, TextField, makeStyles } from "@material-ui/core";
import { Autocomplete, FilterOptionsState, AutocompleteProps } from "@material-ui/lab";

export type Option = {
  label: string;
  value: string;
};

type CreatableOption = Option & { isNew?: boolean };

export type MuiAutocompleteProps = {
  inputLabel?: string;
  error?: boolean;
  required?: boolean;
  isDisabled?: boolean;
  value?: Option | string;
  onChange?: (value: string) => void;
  options: Option[];
  placeholder?: string;
  creatable?: boolean;
} & Omit<
  AutocompleteProps<Option, false, false, false>,
  "value" | "onChange" | "options" | "disabled" | "renderInput"
>;

export function MuiAutocomplete(props: MuiAutocompleteProps) {
  const {
    inputLabel,
    error,
    required,
    isDisabled,
    value,
    onChange,
    options,
    placeholder,
    creatable = false,
    ...autocompleteProps
  } = props;
  const classes = useStyles();

  const selectedOption = getSelectedOption(value, options);

  const handleChange = (event: any, newValue: Option | string | null) => {
    const extractedValue = extractValueFromSelection(newValue);
    onChange?.(extractedValue);
  };

  const filterOptions = (options: Option[], params: FilterOptionsState<Option>) => {
    if (creatable) {
      return createFilteredOptionsWithAddOption(options, params.inputValue);
    }
    return options.filter((option) =>
      option.label.toLowerCase().includes(params.inputValue.toLowerCase())
    );
  };

  return (
    <Box className={classes.root}>
      <Autocomplete
        value={selectedOption}
        onChange={handleChange}
        options={options}
        filterOptions={filterOptions}
        getOptionLabel={getOptionLabel}
        getOptionSelected={getOptionSelected}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        disabled={isDisabled}
        noOptionsText={locale.NoMoreSearchResults}
        renderInput={(params) => (
          <TextField
            {...params}
            label={inputLabel}
            error={error}
            required={required}
            placeholder={placeholder}
            fullWidth
          />
        )}
        {...autocompleteProps}
      />
    </Box>
  );
}

// Helper functions
function getSelectedOption(
  value: Option | string | undefined,
  options: Option[]
): Option | null {
  if (typeof value === "string") {
    return (
      options.find((opt) => opt.value === value) ||
      (value ? { label: value, value: value } : null)
    );
  }
  return value || null;
}

function extractValueFromSelection(newValue: Option | string | null): string {
  if (typeof newValue === "string") {
    return newValue;
  }

  if (newValue) {
    const creatableOption = newValue as CreatableOption;
    return creatableOption.value;
  }

  return "";
}

function createFilteredOptionsWithAddOption(
  options: Option[],
  inputValue: string
): CreatableOption[] {
  const filtered: CreatableOption[] = options.filter((option) =>
    option.label.toLowerCase().includes(inputValue.toLowerCase())
  );

  const isExisting = options.some((option) => inputValue === option.label);

  if (inputValue !== "" && !isExisting) {
    filtered.push({
      label: `${locale.Add} "${inputValue}"`,
      value: inputValue,
      isNew: true
    } as CreatableOption);
  }

  return filtered;
}

function getOptionLabel(option: Option | string): string {
  return typeof option === "string" ? option : option.label;
}

function getOptionSelected(option: Option, value: Option): boolean {
  return option.value === value.value;
}

const useStyles = makeStyles(() => ({
  root: {
    width: "100%"
  }
}));
